/**
 * THE ONE SURFACE.
 *
 * Founder, 2026-08-08: "hopefully student works on single url or app or whatever, not a library of
 * pages." Twelve HTML files were twelve pages, and a candidate who moved between them lost his
 * scroll, his selection and any half-finished drawing every time.
 *
 * So this is ONE surface with MODES. The distinction that matters is not visual — it is that a mode
 * which is not on screen is still MOUNTED. Its canvas keeps its strokes, its list keeps its scroll,
 * and coming back to it is instant because nothing was rebuilt. That is why modes are hidden with
 * CSS rather than unmounted: `{active && <Mode/>}` would be simpler to read and would throw away the
 * candidate's work every time he glanced at something else.
 *
 * A mode is only mounted once it has been VISITED. Mounting all five up front would pay for a 3D
 * scene the candidate may never open, on a phone, over his own data.
 */

import { useState, useCallback, useEffect, useRef, Suspense } from 'react';
import { GateProvider, useTheme, useOnlineHint, Value, AutoTable }
  from '../../../packages/ankr-glass/src/react.jsx';
import { API } from './surfaces.jsx';
import Enrol from './enrol.jsx';
import LightCards from './lightcards.jsx';
import InstallPanel from './install.jsx';
import { identity, legacyLearner, authHeaders, register, signin, recover, signOut, enrolments, isDemo } from './identity.mjs';
import { computed, cited, refused, absent, failed }
  from '../../../packages/ankr-provenance/src/provenance.mjs';

import Ship from './ship.jsx';
import Draw from './draw.jsx';
import PracticeSurface from './practice.jsx';
import SeaReadiness from './sea.jsx';
import LearnSurface from './learn.jsx';
import ProjectSurface from './projectsurface.jsx';

/**
 * THE MODES, and the one thing that must never happen to this list: a surface a candidate can use
 * today must not become unreachable because a nicer version of a DIFFERENT surface shipped.
 *
 * Five of these are pages that already work — /twin, /draw, /studio, /stability — engines that mark,
 * build and compute, standing since 2026-08-01 and each still a working deep link on its own. They
 * are HOSTED here (same origin, `?embed=1` so the page drops its own header), exactly as the surface
 * this one replaces hosted them. Nothing is reimplemented: a second copy of a marking rule is how the
 * rule a candidate is graded by stops being the rule anybody is reading.
 *
 * `src` is therefore not a placeholder for "not built yet". It is the statement that this capability
 * lives in its own page and is composed in here — which is the additive route (FP-012) and the only
 * honest one while the native migration runs surface by surface.
 */
/* THE STUDENT'S LOOP — CONFIRMED ON A PHONE by the founder, 2026-08-10: "checked on my phone, the
   five are right."

   That matters more than it looks. This list was the system's proposal until somebody used it; now
   it is a ruling, and the distinction is the same one CLASS_BASIS carries for the ship speeds — a
   figure a human has actually looked at is a different kind of fact from one nobody has. Recording
   which is which in the data means nobody has to remember.

   Everything else is a hosted engine and genuinely secondary, so it lives behind More rather than
   competing for a thumb's reach. This list is the PRODUCT judgement; the bar below is only its
   layout, and changing the loop must never require touching the layout. */
/* FOUNDER, 2026-08-27: "promote Read into the primary bar". Read sits SECOND, beside Learn, because
   the two are the same act — Learn is the chapter being taught, Read is the signed text of it — and
   a mode nobody can see is a mode nobody uses. Appending it after Progress would have been less
   disruptive to muscle memory and would also have buried it beside the least-used control, which
   defeats the promotion. Seven controls now share one thumb-width row; measured at 390px before
   shipping rather than assumed.

   AND CHECK FOLLOWS, 2026-08-27: "promote Check too". It sits third, immediately after Read, because
   the two are one movement — read the signed text, then put it beside what a live model says. Eight
   controls and More now share the row; measured at 390px and 360px before shipping, for the same
   reason as above. The sheet behind More is not empty: See, Build, Float and Lights still live
   there. */
const PRIMARY = ['learn', 'read', 'check', 'ship', 'draw', 'practice', 'project', 'progress'];

const MODES = [
  /* FOUNDER, 2026-08-27: "rename learn to sources and read to chapters, books shall be books rather
     than learn, and our or any tutor who brings his corpus quoted."
     Two labels both said "chapters" and a student could not tell them apart: this mode shows the
     SOURCE TEXT — Bowditch and its kind, quoted with a citation so a candidate sees he is reading
     Bowditch and not reading us — while the other shows the chapters WE wrote and the founder
     signed. Naming the books "Learn" hid what they are. Sources also holds the case the founder
     names next: any tutor who brings his own corpus, quoted the same way, belongs here — which
     "Books" alone would not cover and "Learn" said nothing about at all. */
  { id: 'learn',    label: 'Sources',  hint: 'The books, and any tutor\'s corpus — quoted, with citations' },
  { id: 'ship',     label: 'Ship',     hint: 'The ship in her own geometry' },
  { id: 'see',      label: 'See',      hint: 'The drawing beside the same steel', src: '/twin' },
  { id: 'draw',     label: 'Draw',     hint: 'Sketch and be marked' },
  { id: 'build',    label: 'Build',    hint: 'Lay the keel and fit her out',      src: '/studio' },
  { id: 'float',    label: 'Float',    hint: 'Stability, worked',                 src: '/stability' },
  { id: 'lights',   label: 'Lights',   hint: "The examiner's light card — identify, quote, act" },
  { id: 'practice', label: 'Practice', hint: 'Past papers' },
  { id: 'project',  label: 'Project',  hint: 'Your own project, a product audit, and your certificate' },
  { id: 'progress', label: 'Progress', hint: 'What you can actually do' },
  /* FOUNDER, 2026-08-27: "can i access these from mari8x.com/study rather than new url".
     Both surfaces shipped on their own addresses — and serve.mjs states the law they broke a few
     lines above where it serves this shell: a new capability becomes a MODE, not a thirteenth URL.
     Added in BOTH places, here and in study.html: the built shell is what a candidate meets, and
     study.html is the FP-010 floor served when app/dist has not been built. A mode that exists in
     only one of them is a capability that vanishes on a fresh checkout. */
  { id: 'read',     label: 'Chapters', hint: 'What we teach — every chapter signed by a named person', src: '/teaching' },
  { id: 'check',    label: 'Check',    hint: 'Two answers to your question, side by side', src: '/compare' },
];

/**
 * THE BOTTOM BAR — mobile navigation where a thumb already rests.
 *
 * FOUNDER asked whether a hamburger would help. It would not, and for a reason specific to this
 * product: mode-switching IS the work here. A cadet moves Learn → Draw → Practice → Learn all
 * session, and a hamburger charges a tap plus a mental step every time. It also undoes the
 * one-surface ruling — visible modes are what make this read as ONE thing with facets rather than a
 * list of pages — and hidden navigation is undiscovered navigation: a new candidate has to SEE that
 * Draw and Practice exist, and most will not open a menu to find out.
 *
 * A bottom bar is the opposite trade: always visible, always in reach, and it costs only the space
 * it occupies. The three hosted engines sit behind More because they are where a candidate GOES,
 * not where he lives.
 */
/* ONE SHEET, USED BY BOTH NAVS. The phone has had a More sheet since this shell shipped; the
   desktop header instead carried all twelve modes in a row that needed 935px and had 632, so two of
   them were reachable only by a horizontal scroll nobody discovers. Giving desktop its own copy of
   this markup would be a second copy of the same control — the fault this file warns about for
   marking rules, and it rots the same way. So it is lifted out and both call it. */
function ModeSheet({ rest, mode, setMode, onClose }) {
  return (
    <>
      {/* Tapping away closes it. A sheet that can only be dismissed by its own control is a
          sheet people feel trapped in. */}
      <div className="shell-sheet-scrim" onClick={onClose} />
      <div className="shell-sheet g-glass" role="dialog" aria-label="More modes">
        <div className="g-label shell-sheet-h">The engines</div>
        {rest.map((m) => (
          <button key={m.id} className={`shell-sheet-row ${mode === m.id ? 'on' : ''}`}
            onClick={() => { setMode(m.id); onClose(); }}>
            <span className="shell-sheet-label">{m.label}</span>
            <span className="shell-sheet-hint g-dim">{m.hint}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/* ── THE BAR IS THE COURSE'S, NOT THE PRODUCT'S (2026-08-29) ─────────────────────────────────
 * FOUNDER: "we have so many tabs in a course - isnt that inelegant?" Option T1, V=10080.
 *
 * Twelve modes, none of which declared a course, so every course showed all twelve — a
 * CLAUDECODE101 reader was served Ship, See, Draw, Build, Float and Lights in a course about a
 * command-line tool. Each ladder now declares what it examines and /api/tabs compiles it.
 *
 * THE LEVEL COMES FROM THE URL because it is already there: learn.jsx writes ?lvl= whenever a
 * course is chosen, so the bar reads the same address the rest of the app already keeps. Lifting
 * the state into App would have been a refactor of two components to learn a fact that was in the
 * query string all along.
 *
 * A COURSE THAT DECLARES NOTHING GETS EVERY TAB, and so does a reader whose level we cannot read,
 * and so does the app before the fetch returns. Three ways to end up with the old behaviour, all of
 * them deliberate: this may narrow a bar, it may never empty one. A filter that can hide a surface
 * on a failed request is a filter that loses Chapters when the network hiccups. */
function useCourseTabs() {
  const [sets, setSets] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/tabs', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => { if (alive && d && d.tabs) setSets(d.tabs); })
      .catch(() => { /* keep null — every tab shows, which is yesterday's behaviour */ });
    return () => { alive = false; };
  }, []);
  const level = (() => {
    try { return (new URLSearchParams(window.location.search).get('lvl') || '').toUpperCase(); }
    catch { return ''; }
  })();
  const claimed = sets && level ? sets[level] : null;
  return Array.isArray(claimed) && claimed.length ? claimed : null;
}

/** Narrow a mode list to what the course claims. Null claim → unchanged, always. */
const forCourse = (modes, claimed) => (claimed ? modes.filter((m) => claimed.includes(m.id)) : modes);

function BottomBar({ mode, setMode }) {
  const [more, setMore] = useState(false);
  /* ORDERED BY PRIMARY, not by MODES. filter() keeps the order of the list it filters, so promoting
     Read to second in PRIMARY put it LAST in the bar — beside More, which is exactly where a promoted
     mode should not be. PRIMARY is the product judgement; the bar is its layout, so the layout reads
     the judgement in the order it was written. */
  const claimed = useCourseTabs();
  const primary = forCourse(PRIMARY.map((id) => MODES.find((m) => m.id === id)).filter(Boolean), claimed);
  const rest = forCourse(MODES.filter((m) => !PRIMARY.includes(m.id)), claimed);
  const restActive = rest.some((m) => m.id === mode);

  return (
    <>
      {more && <ModeSheet rest={rest} mode={mode} setMode={setMode} onClose={() => setMore(false)} />}

      <nav className="shell-bottom g-bar" role="tablist" aria-label="Modes">
        {primary.map((m) => (
          <button key={m.id} role="tab" aria-selected={mode === m.id}
            className={`shell-bt ${mode === m.id ? 'on' : ''}`}
            onClick={() => { setMore(false); setMode(m.id); }}>
            <span className="shell-bt-dot" aria-hidden="true" />
            <span className="shell-bt-label">{m.label}</span>
          </button>
        ))}
        <button className={`shell-bt ${restActive ? 'on' : ''}`} aria-expanded={more}
          onClick={() => setMore((v) => !v)}>
          <span className="shell-bt-dot" aria-hidden="true" />
          <span className="shell-bt-label">More</span>
        </button>
      </nav>
    </>
  );
}

function Bar({ mode, setMode, theme, setTheme, online }) {
  /* THE SAME NARROWING AS THE BOTTOM BAR, and it has to be here too. This nav renders PRIMARY
     directly, so filtering only the phone bar would have given one product two answers to "what
     matters in this course" — which is the fault the comment inside BottomBar already records
     about ORDER. Same list, same rule, both widths. */
  const claimedTabs = useCourseTabs();
  /* The desktop header's own More. Its own state, not BottomBar's: the two navs are never on screen
     together (shell.css hides one at 720px), and sharing one flag across a boundary they cannot both
     cross would be a coupling with nothing on the other end. The SHEET is shared — ModeSheet — which
     is the part that would rot if it were copied. */
  const [deskMore, setDeskMore] = useState(false);
  const rest = MODES.filter((m) => !PRIMARY.includes(m.id));
  const restActive = rest.some((m) => m.id === mode);
  /* The install panel is opened from here and draws itself over everything (fixed position), so it
     is held here rather than lifted into App — the shell owns its own chrome. */
  const [install, setInstall] = useState(false);
  /* ── A DEMO MUST NEVER LOOK LIKE AN ACCOUNT ──────────────────────────────────────────────────
     The /preview link opens every ladder, so the product behaves exactly as it would for a paid
     learner — which is the point, and also the hazard: a visitor can walk it for an hour, answer
     papers, and believe he has a record. He has none, and none of it can be recovered. The welcome
     page says so once; this says so for as long as it is true. Shown only when there is no identity
     of the reader's own, so registering makes it disappear rather than having to be dismissed. */
  const demo = isDemo();
  /* The three states named once, so the badge and anything else asking cannot disagree. */
  const me = identity();
  const who = me
    ? { label: me.name || me.principal, cls: 'is-named', title: `Signed in as ${me.name || me.principal}` }
    : demo
      ? { label: 'Demonstration', cls: 'is-demo', title: 'A demonstration identity — open, and nothing is recorded' }
      : { label: 'Not signed in', cls: 'is-none', title: 'Nobody is signed in on this device. Gated chapters will refuse.' };
  return (
    <header className="g-bar g-bar-top shell-bar">
      {demo && (
        <div className="shell-demo" role="status">
          <b>Demonstration</b> — every ladder is open and <b>nothing you do is recorded</b>.{' '}
          {/* ── /study?m=enrol WAS NOT A MODE, SO THIS LANDED ON SOURCES (2026-08-29) ──────────────
                  A visitor on the demonstration is told, correctly, that nothing is recorded and
                  invited to register — and the link put him on the Sources tab, which has no
                  registration form anywhere on it. `enrol` is not one of the twelve mode ids, so
                  ?m= fell back to learn, silently and by design.
                  Third time in two days for the same fault: a reset link that named no tab, a
                  sign-in nothing pointed at, and now the register invitation itself. gate-academy-
                  entry now asserts that every /study deep link in this file names a REAL mode. */}
              <a href="/study/?m=progress">Register for a record of your own</a>
        </div>
      )}
      <div className="shell-bar-in">
        <div className="shell-brand">
          <svg viewBox="0 0 100 100" width="26" height="26" aria-hidden="true">
            <defs><mask id="sb"><rect width="100" height="100" fill="#fff" />
              <path d="M32.34,54.61 A20,20 0 0 1 40.30,46.51" stroke="#000" strokeWidth="17" fill="none" />
            </mask></defs>
            <g fill="none" stroke="var(--g-accent)" strokeWidth="9" transform="rotate(90 50 50)">
              <circle cx="50" cy="36" r="20" /><circle cx="50" cy="64" r="20" mask="url(#sb)" />
            </g>
          </svg>
          {/* FP-001 PRODUCT ISOLATION. This is mari8x ACADEMY — the digital maritime university —
              and it is a DIFFERENT PRODUCT from the mari8x maritime platform, which has its own
              services, its own audience and its own ~1,180 widgets. Shared parentage in the name is
              not shared identity: no shared auth, no shared data, no cross-product principals. The
              word Academy is not decoration here; dropping it is how two products become one by
              accident. */}
          <span className="shell-name">mari<b>8x</b> <span className="shell-sub">Academy</span></span>
        </div>

        {/* Tabs, not links. A candidate never leaves this document. */}
        {/* DESKTOP SHOWS ALL TWELVE, IN THE ORDER PRIMARY RULES — not in declaration order.
              "Promote Read into the primary bar" reordered PRIMARY, which the bottom bar reads; this
              row mapped MODES directly, so on a desktop the promoted mode sat second-to-LAST while on
              a phone it sat second. One product giving two contradictory answers to "what matters
              here". The walkthrough film caught it, because this header is what a desktop viewer
              actually sees. No More sheet exists at this width, so the rest simply follow. */}
          <nav className="shell-modes" role="tablist" aria-label="Modes">
            {forCourse(PRIMARY.map((id) => MODES.find((m) => m.id === id)).filter(Boolean), claimedTabs).map((m) => (
            <button key={m.id} role="tab" aria-selected={mode === m.id} title={m.hint}
              className={`g-btn g-btn-quiet shell-mode ${mode === m.id ? 'on' : ''}`}
              onClick={() => setMode(m.id)}>
              {m.label}
            </button>
          ))}
        </nav>
            {/* OUTSIDE THE SCROLLING ROW, DELIBERATELY. Inside it, More was the LAST child of an
                overflow-x:auto container, so the 96px clipped at 1280 were More ITSELF — and a More
                nobody can see is worse than no More at all. The modes may scroll; the control that
                reveals the rest may not. */}
            <button className={`g-btn g-btn-quiet shell-mode ${restActive ? 'on' : ''}`}
              aria-expanded={deskMore} onClick={() => setDeskMore((v) => !v)}
              title="See, Build, Float, Lights">More</button>
            {deskMore && <ModeSheet rest={rest} mode={mode} setMode={setMode} onClose={() => setDeskMore(false)} />}

        <div className="shell-right">
          {/* ── WHO IS SIGNED IN, ALWAYS VISIBLE ─────────────────────────────────────────────
              FOUNDER, 2026-08-18: "we need a place where it shows who is the user — i mean demo
              or me or vivek or which student."
              The app already showed an identity, but only inside the enrolment panel, so the
              answer to "who am I right now" required navigating somewhere to find out. It cost
              the founder an hour: he had been granted every course, could not see chapters 6 and
              7, and the reason was that the browser held no identity at all — a state nothing on
              screen was reporting. A gated page said "enrol", which is the wrong remedy for a man
              already enrolled and simply not signed in.
              Three states, deliberately distinct: a named identity, a demonstration, or nobody. */}
          {/* ── "NOT SIGNED IN" WAS A STATEMENT WITH NOWHERE TO GO (2026-08-28) ─────────────
              FOUNDER, having been refused by his own roster: "i dont get any login on academy
              screen, how to get tgat?"

              He was right, and looking for a login was the correct thing to do. There is no control
              anywhere called Sign in. The form lives at the BOTTOM of the Progress tab, inside the
              enrolment panel, behind a button reading "Already enrolled?" — a phrase that answers a
              question nobody asked out loud. The one element on screen that says he is not signed in
              was plain text.

              So the chip becomes the way in whenever it is reporting nobody. It keeps the badge and
              gains a verb, and it deep-links to the tab that holds the form rather than to the app's
              front door. A state that names a problem and offers no route to fixing it is making the
              reader do the work; he did the work, and that was the bug. */}
          {me || demo ? (
            <span className="shell-who" title={who.title}>
              <span className={`shell-who-dot ${who.cls}`} aria-hidden="true" />
              <span className="shell-who-label">{who.label}</span>
            </span>
          ) : (
            <a className="shell-who shell-who-cta" href="/study/?m=progress"
               title="Nobody is signed in on this device. Gated chapters will refuse. Opens the panel where you sign in or enrol.">
              <span className={`shell-who-dot ${who.cls}`} aria-hidden="true" />
              <span className="shell-who-label">{who.label} — <b>Sign in</b></span>
            </a>
          )}
          <ProgressRing />
          {/* A HINT, never a verdict — navigator.onLine says an interface exists, not that the
              server is reachable. It informs; it never disables anything. */}
          {!online && <span className="g-chip g-chip-null" title="Your device reports no network.
            Anything already downloaded still works.">offline</span>}
          {/* GET THE APP — deliberately in the bar and not buried in More. Its main use is a room
              full of cadets copying the address off a projected screen, which only works if the
              person driving can find it in one look. */}
          <button className="g-btn g-btn-quiet shell-get" onClick={() => setInstall(true)}
            title="Get the Academy on your phone — one address, no store">
            <span className="shell-get-glyph" aria-hidden="true">▣</span>
            <span className="shell-get-label">Get the app</span>
          </button>
          <button className="g-btn g-btn-quiet shell-theme"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
            {theme === 'dark' ? '☾' : '☀'}
          </button>
        </div>
      </div>
      {install && <InstallPanel onClose={() => setInstall(false)} />}
    </header>
  );
}

/**
 * THE RING — carried across from the surface this one replaces, and carried across deliberately: a
 * candidate who has passed nine chapters must not open a new shell and find no sign of them.
 *
 * The learner key stays `coc-learner`, unchanged. Renaming it would not lose his RECORD — that is
 * server-side — but it would lose HIM, and from where he is sitting those look identical.
 *
 * With no learner there is no ring. The surface this replaces drew "0 of 18" for an unknown
 * candidate, and 0 is a claim: it says he has attempted nothing and been passed on nothing. We do
 * not know that. The chapter COUNT is context and is shown; the progress claim is withheld until
 * there is somebody to make it about. An outage costs the number, never a mode.
 */
function ProgressRing() {
  const [state, setState] = useState(null);
  const [chapters, setChapters] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const l = await (await fetch(`${API}/api/ladders`)).json();
        const rung = (l.ladders || []).find((x) => x.level === '2M') || (l.ladders || [])[0];
        if (live && rung) setChapters(rung.chapters);
      } catch { /* context only */ }
      /* A registered learner is read by his TOKEN — the server derives whose record it is, and no
         longer accepts a learner name on trust. A device still carrying the old local key keeps
         working unchanged; that path is never created again. */
      const me = identity();
      const legacy = legacyLearner();
      if (!me && !legacy) return;
      try {
        const s = await (await fetch(
          me ? `${API}/api/mastery/state` : `${API}/api/mastery/state?learner=${encodeURIComponent(legacy)}`,
          { headers: authHeaders() })).json();
        // typeof, not Number(): Number(null) is 0 and finite, and would draw an empty ring as fact.
        if (live && typeof s.passed === 'number') setState(s);
      } catch { /* context only */ }
    })();
    return () => { live = false; };
  }, []);

  const total = state ? state.total : chapters;
  if (total == null) return null;
  const C = 2 * Math.PI * 15;
  const done = state ? state.passed / (state.total || 1) : 0;

  return (
    <div className="shell-ring" title={state
      ? `${state.passed} of ${state.total} chapters passed — a chapter passes on two demonstrations at the 'apply' rung, not on reading it`
      : `${total} chapters. Your progress appears once you have attempted something.`}>
      <svg viewBox="0 0 36 36" width="32" height="32" aria-hidden="true">
        <circle cx="18" cy="18" r="15" fill="none" strokeWidth="3.5" stroke="var(--g-edge-strong)" />
        {state && <circle cx="18" cy="18" r="15" fill="none" strokeWidth="3.5" strokeLinecap="round"
          stroke="var(--g-ok, var(--g-accent))" transform="rotate(-90 18 18)"
          strokeDasharray={C} strokeDashoffset={C * (1 - done)} />}
      </svg>
      <span className="shell-ring-n">
        <b>{state ? state.passed : '—'}</b>of {total}
      </span>
    </div>
  );
}

/**
 * A capability that lives in its own page, composed in here.
 *
 * The iframe is created ONCE and then kept, which the mode mechanism already guarantees: a mode is
 * hidden rather than unmounted, so the marking pad keeps a half-finished sketch and the studio keeps
 * a half-built ship while the candidate looks something up. Re-creating the frame on every switch
 * would reload the page and make a tab feel like navigation, which is the exact feeling this surface
 * exists to remove.
 *
 * `?embed=1` is the page's own contract, not a hack — each of these pages hides its header under it,
 * because the shell above already carries the brand and the context.
 */
function Hosted({ src, label, theme }) {
  const ref = useRef(null);

  /* THE SEAM, CLOSED FROM THE SHELL SIDE.
     Each hosted page was built standalone and carries its own palette — twin near-black blue, draw
     green-tinted, stability a LIGHT serif document — so tabbing Learn → Draw → Float crossed three
     visual identities inside one address, and the shell's theme toggle reached none of them.

     The frames are SAME ORIGIN, so the shell can reach in and (a) link the embed skin, which remaps
     each page's own --bg/--ink/--line to the shared tokens, and (b) carry the theme across. Doing it
     here rather than in four pages keeps one place to change and leaves every deep link exactly as it
     was: open /stability directly and it is still the light document it was designed as.

     Everything is guarded. A frame that is not ready, not same-origin, or gone yields nothing and the
     page keeps its own appearance — a seam is a blemish, and it must never become a blank mode. */
  const dress = useCallback(() => {
    const doc = (() => { try { return ref.current?.contentDocument; } catch { return null; } })();
    if (!doc?.documentElement) return;
    doc.documentElement.setAttribute('data-theme', theme);
    if (!doc.getElementById('ankr-embed-skin')) {
      const l = doc.createElement('link');
      /* /study/embed-skin.css, not /embed-skin.css. The bare path falls through to the SPA catch-all
         and comes back text/html with a 200, which every browser silently drops — so the skin has
         never reached ANY embedded mode, not just the new ones. nginx now serves the bare path too,
         but Cloudflare had already cached the HTML against it, so this uses the path that is clean at
         the edge. Same fault, same day, as the stylesheet on read.html and compare.html. */
      l.id = 'ankr-embed-skin'; l.rel = 'stylesheet'; l.href = '/study/embed-skin.css';
      doc.head?.appendChild(l);
    }
  }, [theme]);

  // Re-dress on theme change too: the frame is never reloaded, so a toggle must reach the live document.
  useEffect(() => { dress(); }, [dress]);

  return (
    <iframe ref={ref} className="shell-hosted" title={label} onLoad={dress}
      src={src + (src.includes('?') ? '&' : '?') + 'embed=1'} />
  );
}

/**
 * The first real surface, and it is deliberately the provenance one: every other surface inherits
 * how this reads. If a candidate cannot tell a refusal from an outage here, nothing built on top
 * will fix it.
 */
function ProvenanceLegend() {
  const rows = [
    { item: 'Design GM',        value: computed(2.80),
      rule: cited('IS Code 2008 §2.2', 'IS Code 2008'), source: cited('M.V. ABHYAS offsets', 'offsets') },
    { item: 'KG at loaded',     value: computed(8.312),
      rule: cited('KG = KM − GM − FSC', 'IS Code'),     source: cited('declared condition', 'condition') },
    { item: 'Hindship KM',      value: refused('Rewari & Joseph hold the copyright; the figure cannot be reproduced'),
      rule: absent(),                                    source: absent() },
    { item: 'Live fleet draught', value: failed('no network — last sync unknown'),
      rule: absent(),                                    source: absent() },
  ];
  return (
    <div className="shell-col g-rise">
      <div className="g-glass shell-panel">
        <div className="g-label">Ship stability · worked values</div>
        <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>How this system knows</h2>
        <p className="g-body">
          Every figure carries how it was arrived at. <b>Computed</b> is derived from declared
          geometry. <b>Cited</b> quotes a named source you can follow. <b>Refused</b> means the
          system could have answered and declined — and says why. <b>Failed</b> is an outage, which
          is a different thing entirely and is never dressed as an absence.
        </p>
        <AutoTable widget={{ slug: 'stability-worked-values' }} rows={rows} />
      </div>

      <div className="g-glass shell-panel">
        <div className="g-label">A single value, every state</div>
        <div className="shell-states">
          {[['computed', computed(1.72)], ['cited', cited('SOLAS II-1 Reg 5', 'SOLAS')],
            ['refused', refused('no loading condition declared for this vessel')],
            ['absent', absent()], ['failed', failed('engine unreachable')]].map(([k, v]) => (
            <div key={k} className="shell-state">
              <span className="g-label">{k}</span>
              <Value of={v} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ENROL lives in enrol.jsx — moved 2026-08-16 so the gated panel can offer it at the
   point of refusal instead of sending the learner to look for it. */

/**
 * HOLDINGS — the principal this device carries, and the tracks against it.
 *
 * Added 2026-08-13 after the founder was granted a track at the command line and had no way inside
 * the product to see whether it had landed on him. The principal is DERIVED from name and contact,
 * so a grant made against "Capt Anil Sharma" does not reach someone who enrolled as "Capt. Anil
 * Sharma" — and nothing on screen would have said so. It is shown in full, monospaced, because a
 * truncated hash cannot be compared against the one in a grant.
 *
 * An empty list is stated plainly. "No enrolment yet" is a true and useful thing to read; an empty
 * space is a question.
 */
function Holdings() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    enrolments(API).then((j) => { if (live) setD(j); }).catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, []);
  if (err) return <p className="g-body enr-note"><Value of={failed(err)} /></p>;
  if (!d) return null;
  const held = d.enrolments || [];
  return (
    <div className="hold-box">
      <div className="g-label">This device carries</div>
      <p className="hold-id g-mono">{d.principal}</p>
      {held.length ? (
        <ul className="hold-list">
          {held.map((e) => (
            <li key={e.track}>
              <b>{e.track}</b> — {e.expires === 'never' ? 'does not expire' : `until ${e.expires}`}
              <span className="g-dim"> · {e.source}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="g-body g-dim">No enrolment yet on this identity. The free foundation tier needs none.</p>
      )}
      {d.policy?.enforce === false && (
        <p className="g-body g-dim">Nothing is being refused yet — enrolment is recorded but not enforced.</p>
      )}
    </div>
  );
}

/**
 * THE STUDENT RECORD — rank, ticket, sea time, the numbers on his documents.
 *
 * The form is COMPILED FROM THE SERVER'S DECLARATION, not hand-listed here. Two hand-kept copies of a
 * field list do not drift as a risk, they drift as a certainty (FP-016), and the failure is silent:
 * a field added on the server that no surface ever shows is a field nobody fills in.
 *
 * WHY IT ASKS FOR SO LITTLE, AND SAYS WHY FOR EACH. Every field here has to earn its place by being
 * used for something — sea time is an exam ELIGIBILITY fact, an exam date is what gives a study plan
 * a deadline to work back from. A form that collects what it does not use is asking a seafarer to
 * hand over his document numbers for filing, which is exactly the habit this product should not have.
 */
function StudentRecord() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [edit, setEdit] = useState(null);        // null = showing, object = editing
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = () => fetch(`${API}/api/record`, { headers: authHeaders() })
    .then(async (r) => {
      const j = await r.json().catch(() => ({}));
      /* A record that cannot be READ is not a record that is empty, and the difference matters more
         here than anywhere else in the app: showing a blank form would invite him to type it all
         again over the top of something still perfectly intact. */
      if (j.unreadable) throw new Error(j.error);
      if (!r.ok) throw new Error(j.error || 'the record did not answer');
      return j;
    })
    .then(setD).catch((e) => setErr(e.message));

  useEffect(() => { load(); }, []);

  if (err) return (
    <div className="g-glass shell-panel">
      <div className="g-label">Your details</div>
      <p className="g-body"><Value of={failed(err)} /></p>
    </div>
  );
  if (!d) return null;

  const F = d.fields || {};
  const have = d.record || {};
  const c = d.completeness || { have: 0, of: 0, pct: 0 };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setSaved(false);
    try {
      const r = await fetch(`${API}/api/record`, {
        method: 'POST', headers: { 'content-type': 'application/json', ...authHeaders() },
        body: JSON.stringify(edit),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'the record was not saved');
      setD((v) => ({ ...v, record: j.record, updated_at: j.updated_at, revisions: j.revisions, completeness: j.completeness }));
      setEdit(null); setSaved(true);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  if (edit) {
    const set = (k) => (e) => setEdit((v) => ({ ...v, [k]: e.target.value }));
    return (
      <form className="g-glass shell-panel" onSubmit={save}>
        <div className="g-label">Your details</div>
        <h3 className="g-h2 enr-h">What the Academy knows about you</h3>
        <p className="g-body">
          Only you can read this. It is encrypted where it is stored, and every field is optional —
          leave anything blank and nothing here will nag you for it.
        </p>
        <div className="rec-grid">
          {Object.entries(F).map(([k, f]) => (
            <label className="rec-field" key={k}>
              <span className="rec-label">{f.label}</span>
              {f.track ? (
                <select className="enr-in" value={edit[k] ?? ''} onChange={set(k)}>
                  <option value="">—</option>
                  {(d.tracks || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              ) : f.int ? (
                <input className="enr-in" type="number" min={f.int[0]} max={f.int[1]}
                  value={edit[k] ?? ''} onChange={set(k)} />
              ) : f.day ? (
                <input className="enr-in" type="date" value={edit[k] ?? ''} onChange={set(k)} />
              ) : f.max > 200 ? (
                <textarea className="enr-in rec-area" rows={3} maxLength={f.max}
                  value={edit[k] ?? ''} onChange={set(k)} />
              ) : (
                <>
                  <input className="enr-in" list={f.suggest?.length ? `sg-${k}` : undefined}
                    maxLength={f.max} value={edit[k] ?? ''} onChange={set(k)} />
                  {!!f.suggest?.length && (
                    <datalist id={`sg-${k}`}>{f.suggest.map((s) => <option key={s} value={s} />)}</datalist>
                  )}
                </>
              )}
              {/* The reason a field exists, shown next to the field. A seafarer being asked for a CDC
                  number is owed the sentence explaining what it is for. */}
              {f.why && <span className="rec-why">{f.why}</span>}
            </label>
          ))}
        </div>
        {err && <p className="g-body enr-err"><Value of={failed(err)} /></p>}
        <p className="enr-note">
          <button className="g-btn g-btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>{' '}
          <button type="button" className="g-btn" onClick={() => { setEdit(null); setErr(''); }}>Cancel</button>
        </p>
      </form>
    );
  }

  const shown = Object.entries(F).filter(([k]) => have[k] !== undefined && have[k] !== null);
  return (
    <div className="g-glass shell-panel">
      <div className="g-label">Your details</div>
      <h3 className="g-h2 enr-h">What the Academy knows about you</h3>
      {shown.length === 0 ? (
        <p className="g-body">
          Nothing yet — and nothing here is required to study. It is worth filling in when you have a
          date for the exam, because that is what turns a syllabus into a plan.
        </p>
      ) : (
        <AutoTable widget={{ slug: 'student-record' }} empty="—"
          rows={shown.map(([k, f]) => ({
            item: f.label,
            value: computed(String(have[k])),
            rule: cited('you told us', 'your record'),
            source: cited(d.updated_at ? d.updated_at.slice(0, 10) : '—', 'last changed'),
          }))} />
      )}
      <p className="g-body g-dim enr-note">
        {c.have} of {c.of} filled in · stored encrypted, readable only by you
        {d.revisions > 1 && <> · {d.revisions} revisions kept, nothing overwritten</>}
        {saved && <> · saved</>}
        {' '}
        <button className="g-btn g-btn-quiet enr-out"
          onClick={() => setEdit(Object.fromEntries(Object.keys(F).map((k) => [k, have[k] ?? ''])))}>
          {shown.length ? 'Edit' : 'Fill it in'}
        </button>
      </p>
    </div>
  );
}

/**
 * PROGRESS — what the candidate can actually do, which is a different question from what he has read.
 *
 * Every figure here comes from the mastery engine. The one thing this surface must never do is fill
 * a quiet moment with an encouraging number: with no learner there is no progress to report, and it
 * says so instead of drawing an empty ring and calling it a start.
 */
function ProgressSurface() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const me = identity();
  const id = me ? (me.name || me.principal) : legacyLearner();

  useEffect(() => {
    if (!id) return;
    let live = true;
    fetch(me ? `${API}/api/mastery/state` : `${API}/api/mastery/state?learner=${encodeURIComponent(id)}`,
      { headers: authHeaders() })
      .then((r) => r.json())
      .then((s) => { if (live) (typeof s.passed === 'number' ? setState(s) : setError('the engine answered, but not with a state')); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [id]);

  if (!id) return (
    <div className="shell-col g-rise">
    <div className="g-glass shell-panel">
      <div className="g-label">Progress</div>
      <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>Nothing to report yet</h2>
      <p className="g-body">
        Progress is recorded against a learner, and this device is not carrying one. Answer something
        in <b>Learn</b> or submit a sketch in <b>Draw</b> and it starts from there. A chapter is
        passed on <b>two demonstrations at the ‘apply’ rung</b> — never on having read it.
      </p>
    </div>
    <Enrol />
    {/* Readiness for sea does not depend on carrying a learner record. A cadet who has answered
        nothing yet still sails, and still needs to know what is aboard. */}
    <SeaReadiness />
    </div>
  );

  const rows = state ? [
    { item: 'Chapters passed',    value: computed(`${state.passed} of ${state.total}`),
      rule: cited('two ‘apply’ demonstrations per chapter', 'mastery gate'), source: cited(id, 'learner') },
    { item: 'Foundations passed', value: computed(`${state.foundations_passed} of ${state.foundations_total}`),
      rule: cited('the first seven chapters are load-bearing', '2M ladder'), source: cited(id, 'learner') },
    { item: 'Next chapter',       value: state.next_chapter
        ? computed(`${state.next_chapter.n}. ${state.next_chapter.title}`)
        : refused('every chapter on this ladder is passed'),
      rule: cited('routed by unmet prerequisite, not by order', '2M ladder'), source: cited(id, 'learner') },
  ] : [];

  return (
    <div className="shell-col g-rise">
      <div className="g-glass shell-panel">
        <div className="g-label">Progress · {id}</div>
        <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>What you can actually do</h2>
        {error
          ? <p className="g-body"><Value of={failed(error)} /></p>
          : <AutoTable widget={{ slug: 'mastery-state' }} rows={rows} empty="Reading your record…" />}
        {/* WHAT HE ACTUALLY HOLDS, shown rather than assumed. A grant is made at the command line and
            lands on a DERIVED principal, so the only way a learner knows it reached HIM — and not an
            identity one character away — is to see his own principal beside the tracks against it.
            Without this he has to take our word for it, which is the one thing this product does not
            ask anyone to do. */}
        {me && <Holdings />}
        {me && (
          <p className="g-body g-dim enr-note">
            Enrolled as <b>{me.name || me.principal}</b> — the same identity at coc.ankr.in.{' '}
            {/* Sign out clears the DEVICE, not the record. Saying so removes the fear that makes
                people never sign out on a shared machine, which is the real risk in a classroom. */}
            <button className="g-btn g-btn-quiet enr-out"
              onClick={() => { signOut(); window.location.reload(); }}>
              Sign out of this device
            </button>{' '}
            Your record stays; enter the same name and contact to return to it.
          </p>
        )}
      </div>
      {/* WHO SET THE BAR. Shown to every learner, enrolled or not, and next to the figures it
          qualifies — "4 of 21 passed" means less if nobody has said who wrote the criteria. The
          sentence and the count both come from the engine, which computes them from the rungs
          themselves, so the figure improves as attestation proceeds and cannot drift from the data.
          Founder's ruling 2026-08-13: declare it once here rather than mark 57 rungs individually. */}
      {state?.criteria_authorship && (
        <div className="g-glass shell-panel">
          <div className="g-label">Who wrote what you are measured against</div>
          <p className="g-body" style={{ marginTop: 'var(--g-2)' }}>{state.criteria_authorship.says}</p>
          <p className="g-body g-dim enr-note">
            <span className="g-mono">
              {state.criteria_authorship.attested} attested ·{' '}
              {state.criteria_authorship.provisional} provisional ·{' '}
              {state.criteria_authorship.unmarked} ours, unreviewed
            </span>
          </p>
        </div>
      )}
      {/* Only for an enrolled learner — a record needs an identity to belong to, and the surface
          says so by simply not being there rather than by offering a form that cannot save. */}
      {me && <StudentRecord />}
      <SeaReadiness />
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useTheme();
  const online = useOnlineHint();
  /* ?m= makes a mode a shareable address without making it a PAGE. An unknown value falls back to
     Learn rather than opening on nothing — a bad link should still land somewhere useful. */
  const [mode, setMode] = useState(() => {
    const want = new URLSearchParams(window.location.search).get('m');
    return MODES.some((m) => m.id === want) ? want : 'learn';
  });
  // A mode is mounted on first visit and never unmounted after — this Set is the whole mechanism.
  const [visited, setVisited] = useState(() => new Set([mode]));

  /* The active tab is scrolled into view whenever the mode changes. Without this a candidate on a
     phone can tap a tab at the edge and be left looking at a different one — which is the crop the
     founder reported, showing up as a navigation fault rather than a styling one. */
  useEffect(() => {
    const el = document.querySelector('.shell-mode.on');
    el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [mode]);

  const go = useCallback((id) => {
    setVisited((v) => (v.has(id) ? v : new Set(v).add(id)));
    setMode(id);
    /* The address carries the mode, so a candidate can send somebody exactly what he is looking at.
       replaceState, not pushState: Back should leave the Academy, not walk him through eight tabs. */
    const u = new URL(window.location);
    u.searchParams.set('m', id);
    window.history.replaceState(null, '', u);
  }, []);

  /* A hosted page brings its own margins and wants the full width; a native surface wants the
     shell's column. The layout follows what is on screen rather than being fixed for both. */
  const hosted = MODES.find((m) => m.id === mode)?.src;

  return (
    <GateProvider user={{ id: 'candidate', role: 'student' }}>
      <Bar mode={mode} setMode={go} theme={theme} setTheme={setTheme} online={online} />
      <BottomBar mode={mode} setMode={go} />
      <main className={`shell-main ${hosted ? 'shell-main-flush' : ''}`}>
        {MODES.map((m) => visited.has(m.id) ? (
          /* hidden, NOT unmounted — the canvas keeps its strokes and the list keeps its scroll */
          <section key={m.id} role="tabpanel" aria-label={m.label} hidden={mode !== m.id}>
            <Suspense fallback={<div className="gl-empty g-body">Loading {m.label}…</div>}>
              {m.src ? <Hosted src={m.src} label={m.label} theme={theme} />
                : m.id === 'learn' ? <LearnSurface />
                : m.id === 'practice' ? <PracticeSurface />
                : m.id === 'project' ? <ProjectSurface />
                : m.id === 'ship' ? <Ship />
                : m.id === 'draw' ? <Draw />
                : m.id === 'lights' ? <LightCards />
                : m.id === 'progress' ? <ProgressSurface />
                : <ProvenanceLegend />}
            </Suspense>
          </section>
        ) : null)}
      </main>
    </GateProvider>
  );
}
